import React from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';

type Segment = {
  progress: number;
};

type AnimatedSegmentProps = {
  smoothProgress: any;
  segment: Segment;
};

const AnimatedSegment = ({ smoothProgress, segment }: AnimatedSegmentProps) => {
  const width = useTransform(
    smoothProgress,
    [0, segment.progress],
    ['0%', '100%']
  );
  const opacity = useTransform(
    smoothProgress,
    [Math.max(0, segment.progress - 0.01), segment.progress],
    [0, 1]
  );

  return (
    <motion.div
      className="h-full bg-[#02A9F7] absolute inset-0"
      style={{ width, opacity }}
    />
  );
};

type TrackerSideProps = {
  side: 'left' | 'right';
  segments: Segment[];
  smoothProgress: any;
};

const TrackerSide = ({ side, segments, smoothProgress }: TrackerSideProps) => {
  const sidePosition = side === 'left' ? 'left-0' : 'right-0';
  const borderClass = side === 'left' ? 'border-r' : 'border-l';

  return (
    <div
      className={`fixed ${sidePosition} top-0 bottom-0 w-[calc((100vw-1600px)/2)] hidden lg:block z-50 ${borderClass} border-[#1a1a1a] bg-[#0A0A0B]`}
    >
      <div className="absolute inset-0 flex flex-col justify-between py-8">
        {segments.map((segment, index) => (
          <motion.div
            key={`${side}-${index}`}
            className="h-[1px] w-full flex justify-center relative"
          >
            <div className="w-1/5 relative">
              <div className="h-full w-full bg-[#1a1a1a] opacity-20" />
              <AnimatedSegment smoothProgress={smoothProgress} segment={segment} />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const ScrollTracker = () => {
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const segments: Segment[] = Array.from({ length: 20 }, (_, i) => ({
    progress: i / 19,
  }));

  return (
    <>
      <TrackerSide side="left" segments={segments} smoothProgress={smoothProgress} />
      <TrackerSide side="right" segments={segments} smoothProgress={smoothProgress} />
    </>
  );
};

export default ScrollTracker;